import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { PAGE_TITLES } from "~/configs/pageTitles";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import { apiClient } from "../../api/ApiClient";
import ImportKeywordStatusTable, {
  KeywordStatus,
} from "../../components/keywordList/ImportKeywordStatusTable";
import NotificationBlock from "../../components/notificationBlock/NotificationBlock";
import Breadcrumbs from "../../components/ui/breadcrumbs/Breadcrumbs";
import styles from "./KeywordImportPage.module.scss";

const KeywordImportPage = () => {
  const { importId, id } = useParams();

  if (!id) {
    throw Error("No site id provided");
  }

  if (!importId) {
    throw Error("No import id provided");
  }

  const heading = `Keyword Import #${importId}`;
  usePageTitle(heading);

  const { data: keywordImportsById } = useQuery({
    queryKey: ["keywordImportsById", importId, id],
    queryFn: async () => {
      const response = await apiClient.sites.keywordImportsDetail2(
        Number(id),
        Number(importId),
      );

      return response?.data?.keyword_import;
    },
    refetchInterval(query) {
      const data = query?.state?.data;
      if (data?.status === "pending") {
        return 3000;
      }

      return false;
    },
  });

  const isFailed = keywordImportsById?.status === "failed";

  const keywordStatusData =
    keywordImportsById?.mapped_data &&
    Array.isArray(keywordImportsById.mapped_data)
      ? keywordImportsById.mapped_data.map((item: unknown[]) => ({
          keyword: item[0],
          status: item[1],
          error: item[2],
          keyword_attributes: keywordImportsById.keyword_attributes,
        }))
      : [];

  const failedKeywords = keywordStatusData.filter(
    (item) => item.status === "failed",
  );

  const addedKeywords = keywordStatusData.filter(
    (item) => item.status === "complete",
  );

  const pendingKeywords = keywordStatusData
    .map((item) => ({
      ...item,
      status: item.status || "pending",
    }))
    .filter((item) => item.status === "pending");

  const fakeBreadcrumbs = [
    {
      title: PAGE_TITLES.keywordsList,
      link: routes.site.keywords.list({ id }),
    },
    {
      title: PAGE_TITLES.keywordImports,
      link: routes.site.keywords.imports({ id }),
    },
    {
      title: heading,
      link: routes.site.keywords.import({ id, importId }),
    },
  ];

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{heading}</h1>
      <div className={styles.pageBreadcrumbs}>
        {fakeBreadcrumbs.length > 0 && (
          <Breadcrumbs fakeBreadcrumbs={fakeBreadcrumbs} />
        )}
      </div>

      <div className={styles.pageContent}>
        {isFailed && (
          <NotificationBlock
            type="danger"
            message="Import failed, please contact support team "
          />
        )}
        <ImportKeywordStatusTable
          data={addedKeywords as KeywordStatus[]}
          title="Added Keywords"
          noError={true}
        />
        <ImportKeywordStatusTable
          data={failedKeywords as KeywordStatus[]}
          title="Error"
        />
        <ImportKeywordStatusTable
          data={pendingKeywords as unknown as KeywordStatus[]}
          title="Pending"
          noError={true}
        />
      </div>
    </div>
  );
};

export default KeywordImportPage;
